import { cn } from '@/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

/**
 * LA recette d'un contrôle éteint, et la seule orthographe qu'elle ait.
 *
 * Avant BUILD-916 c'était `disabled:opacity-40`, et une opacité ne rend pas un
 * bouton inerte - elle le PÂLIT. Un `bg-bleu-nuit` plein à 40 % reste un
 * aplat de noyau : sur le formulaire de publication du catalogue et sur Sons et
 * images, le bouton mort était plus voyant que les secondaires vivants posés à
 * côté de lui, donc il attirait le clic qu'il refuse. Son texte tombait à
 * 2,25:1 - du blanc à 40 % sur un bleu-nuit à 40 % - c'est-à-dire illisible au
 * moment précis où il faut lire ce que le bouton dit pour comprendre pourquoi il
 * ne répond pas.
 *
 * La recette remplace les trois couleurs au lieu de les diluer : gris clair,
 * bordure grise, encre `gray-600` - 4,55:1, donc lisible, alors qu'un contrôle
 * désactivé est justement ce que WCAG dispense de tout seuil. Un libellé qu'on
 * ne peut pas lire ne dit pas pourquoi il est fermé.
 *
 * Elle passe par le variant `disabled:`, et c'est ce qui la fait gagner sans
 * `!important` : `.disabled\:bg-gray-100:disabled` porte une classe ET une
 * pseudo-classe, donc une spécificité supérieure au `bg-bleu-nuit` de la base.
 *
 * Exportée parce que quatorze boutons de l'administration sont écrits à la main
 * plutôt qu'avec ce composant. Deux orthographes d'« éteint » finiraient par
 * diverger, et la moitié qui dérive est toujours celle que personne ne regarde.
 */
export const DISABLED_INERT =
  'disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-600';

/**
 * PMS button. The base is neutral (black & white); a single noyau carries the
 * meaning - bleu-nuit for the primary action, rouge for destructive. Light-only,
 * 44px min touch target (WCAG 2.5.5), 8px radius, Titillium 600, 150ms ease.
 *
 * Variant names follow the shadcn convention (default/outline/ghost/destructive)
 * so the component API stays familiar; the visuals follow the PMS spec.
 */
const buttonVariants = cva(
  // `cursor-pointer` is explicit on purpose: Tailwind v4's preflight sets
  // `cursor: default` on <button>, so without it nothing signals clickability.
  // `hover:shadow-md` lifts the button - the tint change alone reads as flat.
  `inline-flex cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 font-semibold leading-none tracking-[0.005em] transition-[color,background-color,border-color,box-shadow] duration-150 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:shadow-none ${DISABLED_INERT}`,
  {
    variants: {
      variant: {
        // Solid primary action (PMS "secondary" - plein bleu nuit). Pressing
        // drops the lift: there is no token darker than -deep, and inventing
        // one would leave the palette.
        default:
          'border-bleu-nuit bg-bleu-nuit text-white hover:border-bleu-nuit-deep hover:bg-bleu-nuit-deep active:border-bleu-nuit-deep active:bg-bleu-nuit-deep active:shadow-none',
        // Outlined primary (PMS "primary" - bordure bleu nuit, fills on hover).
        outline:
          'border-bleu-nuit bg-white text-bleu-nuit hover:bg-bleu-nuit hover:text-white active:border-bleu-nuit-deep active:bg-bleu-nuit-deep active:text-white',
        // No visual weight until hover.
        ghost:
          'border-transparent bg-transparent text-bleu-nuit hover:bg-gray-100 active:bg-gray-200',
        // Irreversible action.
        destructive:
          'border-rouge bg-white text-rouge hover:bg-rouge hover:text-white active:border-rouge-deep active:bg-rouge-deep active:text-white',
      },
      size: {
        default: 'h-11 px-5 text-base',
        sm: 'h-9 px-4 text-sm',
        lg: 'h-12 px-6 text-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
export type { ButtonProps };
